import { useEffect, useState, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Users, Plus, Upload, Download, Search, X, Check, AlertTriangle, ChevronDown, ChevronUp, Key, Trash2, Pencil, Unlock,
} from 'lucide-react';
import Layout from '../components/Layout.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useRoleLabel } from '../hooks/useRoleLabel.js';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import BulkUserImport from '../components/BulkUserImport.js';
import MultiCardEdit from '../components/MultiCardEdit.js';
import { TierOverrideForm, isOverrideActive, formatOverride } from '../components/TierOverrideForm.js';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Card { cardId: string; slug: string; shortCode: string; orgSlug: string | null; }
interface SuperAdminUser {
  id: string;
  email: string;
  role: string;
  organisationId: string;
  organisationName: string;
  organisationSlug: string;
  firstName: string;
  lastName: string;
  created_at: string;
  cards: Card[];
  tierOverride?: string | null;
  tierOverrideUntil?: string | null;
  tierOverrideNote?: string | null;
}
interface OrgOption { id: string; name: string; slug: string; }

// ─── Shared styles ────────────────────────────────────────────────────────────

const inputCls = 'block w-full rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition';
const labelCls = 'block text-xs font-medium text-ink-muted mb-1';
const btnPrimary = 'inline-flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity';
const btnGhost = 'inline-flex items-center gap-2 rounded-lg border border-line bg-panel px-3 py-1.5 text-sm text-ink hover:bg-raised hover:text-ink transition-all';
const btnDanger = 'inline-flex items-center gap-2 rounded-lg border border-danger-500/30 bg-danger-100 dark:bg-danger-500/10 px-3 py-1.5 text-sm text-danger-700 dark:text-danger-300 hover:bg-danger-500/20 transition-all';

const ALL_ROLES = ['superadmin', 'owner', 'admin', 'department_lead', 'member'] as const;
type RoleType = typeof ALL_ROLES[number];

function roleBadgeCls(role: string) {
  switch (role) {
    case 'superadmin': return 'bg-danger-100 dark:bg-danger-500/20 text-danger-700 dark:text-danger-300 border border-danger-500/30';
    case 'owner': return 'bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-300 border border-accent-500/30';
    case 'admin': return 'bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-300 border border-accent-500/30';
    case 'department_lead': return 'bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-300 border border-accent-500/30';
    default: return 'bg-raised text-ink-muted border border-line';
  }
}

// ─── DrawerShell ──────────────────────────────────────────────────────────────

function DrawerShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-panel border-l border-line shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-line flex-shrink-0">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-ink-muted hover:bg-raised hover:text-ink transition-all">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
      </div>
    </>
  );
}

// ─── AddUserDrawer ────────────────────────────────────────────────────────────

function AddUserDrawer({ orgs, onClose, onCreated }: { orgs: OrgOption[]; onClose: () => void; onCreated: () => void }) {
  const { t } = useAppTranslation();
  const roleLabel = useRoleLabel();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<RoleType>('member');
  const [organisationId, setOrganisationId] = useState(orgs[0]?.id ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const res = await fetch('/api/superadmin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, role, organisationId }),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) { setError(data.error ?? t('superadmin.errors.createFailed')); return; }
      onCreated();
      onClose();
    } catch {
      setError(t('superadmin.errors.network'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DrawerShell title={t('superadmin.createDrawer.title')} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelCls}>{t('common.email')} *</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t('superadmin.createDrawer.passwordLabel')} *</label>
          <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t('common.role')}</label>
          <select value={role} onChange={(e) => setRole(e.target.value as RoleType)} className={inputCls}>
            {ALL_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>{t('common.fields.organisation')} *</label>
          <select value={organisationId} onChange={(e) => setOrganisationId(e.target.value)} required className={inputCls}>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-danger-100 dark:bg-danger-500/10 border border-danger-500/30 px-3 py-2 text-sm text-danger-700 dark:text-danger-300">
            <AlertTriangle size={14} />{error}
          </div>
        )}
        <div className="flex gap-3 pt-1">
          <button type="submit" disabled={saving} className={btnPrimary}>{saving ? t('common.states.creating') : t('users.create')}</button>
          <button type="button" onClick={onClose} className={btnGhost}>{t('common.cancel')}</button>
        </div>
      </form>
    </DrawerShell>
  );
}

// ─── UserRow ──────────────────────────────────────────────────────────────────

function UserRow({
  user,
  currentUserId,
  isSelected,
  onToggle,
  onUpdated,
  onDeleted,
}: {
  user: SuperAdminUser;
  currentUserId: string;
  isSelected: boolean;
  onToggle: () => void;
  onUpdated: (id: string, updates: Partial<SuperAdminUser>) => void;
  onDeleted: (id: string) => void;
}) {
  const { t } = useAppTranslation();
  const roleLabel = useRoleLabel();
  const [expanded, setExpanded] = useState(false);
  const [roleValue, setRoleValue] = useState(user.role);
  const [roleChanging, setRoleChanging] = useState(false);

  const [showUnlock, setShowUnlock] = useState(false);
  const [showPwInput, setShowPwInput] = useState(false);
  const [pwValue, setPwValue] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Per-card shortcode editing
  const [scValues, setScValues] = useState<Record<string, string>>(
    Object.fromEntries(user.cards.map((c) => [c.cardId, c.shortCode]))
  );
  const [scMsgs, setScMsgs] = useState<Record<string, { type: 'success' | 'error'; text: string }>>({});

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
  const initials = [user.firstName, user.lastName]
    .filter(Boolean)
    .map((n) => n.charAt(0).toUpperCase())
    .join('') || user.email.charAt(0).toUpperCase();

  const isSelf = user.id === currentUserId;

  async function handleRoleChange(newRole: string) {
    if (newRole === roleValue) return;
    setRoleChanging(true);
    try {
      const res = await fetch(`/api/superadmin/users/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole }),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) { alert(data.error ?? t('common.error')); return; }
      setRoleValue(newRole);
      onUpdated(user.id, { role: newRole });
    } finally {
      setRoleChanging(false);
    }
  }

  async function handlePwSave() {
    if (pwValue.length < 8) { setPwMsg({ type: 'error', text: t('superadmin.password.minLength') }); return; }
    setPwSaving(true);
    setPwMsg(null);
    try {
      const res = await fetch(`/api/superadmin/users/${user.id}/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: pwValue }),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) { setPwMsg({ type: 'error', text: data.error ?? t('common.error') }); return; }
      setPwMsg({ type: 'success', text: t('settings.saved') });
      setPwValue('');
      setTimeout(() => { setShowPwInput(false); setPwMsg(null); }, 1200);
    } finally {
      setPwSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      const res = await fetch(`/api/superadmin/users/${user.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) { alert(data.error ?? t('common.error')); setDeleteConfirm(false); return; }
      onDeleted(user.id);
    } finally {
      setDeleting(false);
    }
  }

  async function handleScSave(cardId: string) {
    const sc = scValues[cardId] ?? '';
    if (!/^[A-Za-z0-9]{7}$/.test(sc)) {
      setScMsgs((m) => ({ ...m, [cardId]: { type: 'error', text: t('superadmin.shortCode.validation') } }));
      return;
    }
    setScMsgs((m) => ({ ...m, [cardId]: { type: 'success', text: '…' } }));
    try {
      const res = await fetch(`/api/superadmin/cards/${cardId}/shortcode`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ shortCode: sc }),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) {
        setScMsgs((m) => ({ ...m, [cardId]: { type: 'error', text: data.error ?? t('common.error') } }));
        return;
      }
      setScMsgs((m) => ({ ...m, [cardId]: { type: 'success', text: t('settings.saved') } }));
    } catch {
      setScMsgs((m) => ({ ...m, [cardId]: { type: 'error', text: t('superadmin.errors.network') } }));
    }
  }

  return (
    <>
      <tr className="hover:bg-slate-100 dark:hover:bg-white/[0.025] transition-colors">
        {/* Checkbox */}
        <td className="px-3 py-3 w-8">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggle}
            className="rounded border-line-strong bg-panel text-accent-500 focus:ring-accent-500/30 cursor-pointer"
          />
        </td>
        {/* Name */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent-100 dark:bg-accent-600/20 text-xs font-bold text-accent-700 dark:text-accent-300">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink truncate max-w-[140px]">{fullName}</p>
              {isOverrideActive({ tier: user.tierOverride ?? null, until: user.tierOverrideUntil ?? null, note: null }) && (
                <span
                  title={user.tierOverrideNote ?? ''}
                  className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-accent-100 dark:bg-accent-500/15 px-1.5 py-0.5 text-[10px] font-medium text-accent-700 dark:text-accent-300"
                >
                  <Unlock size={9} aria-hidden="true" />
                  {formatOverride({ tier: user.tierOverride ?? null, until: user.tierOverrideUntil ?? null, note: null })}
                </span>
              )}
            </div>
          </div>
        </td>
        {/* Email */}
        <td className="px-4 py-3 text-sm text-ink-muted truncate max-w-[160px] hidden md:table-cell">{user.email}</td>
        {/* Organisation */}
        <td className="px-4 py-3 text-sm text-ink-muted truncate hidden lg:table-cell">{user.organisationName}</td>
        {/* Rolle */}
        <td className="px-4 py-3">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${roleBadgeCls(roleValue)}`}>
            {roleLabel(roleValue)}
          </span>
        </td>
        {/* Karten */}
        <td className="px-4 py-3">
          {user.cards.length > 0 ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-xs text-accent-700 dark:text-accent-300 hover:text-accent-200 transition-colors"
            >
              {user.cards.length} {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          ) : (
            <span className="text-xs text-ink-faint">0</span>
          )}
        </td>
        {/* Aktionen */}
        <td className="px-4 py-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {/* Role dropdown */}
            {!isSelf && (
              <select
                value={roleValue}
                disabled={roleChanging}
                onChange={(e) => handleRoleChange(e.target.value)}
                className="rounded-lg bg-panel border border-line px-2 py-1 text-xs text-ink focus:outline-none focus:border-accent-500/50 disabled:opacity-50 transition"
              >
                {ALL_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
            )}

            {/* Unlock a plan for this user */}
            <button
              type="button"
              onClick={() => setShowUnlock((v) => !v)}
              title={t('admin.unlock.title', 'Plan freischalten')}
              aria-label={t('admin.unlock.title', 'Plan freischalten')}
              className={btnGhost + ' py-1 px-2 text-xs'}
            >
              <Unlock size={12} />
            </button>

            {/* Password */}
            <button
              type="button"
              onClick={() => { setShowPwInput((v) => !v); setPwMsg(null); setPwValue(''); }}
              className={btnGhost + ' py-1 px-2 text-xs'}
            >
              <Key size={12} />
            </button>

            {/* Delete */}
            {!isSelf && !deleteConfirm && (
              <button type="button" onClick={() => setDeleteConfirm(true)} className={btnDanger + ' py-1 px-2 text-xs'}>
                <Trash2 size={12} />
              </button>
            )}
            {!isSelf && deleteConfirm && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-danger-700 dark:text-danger-300">{t('common.confirm')}?</span>
                <button
                  type="button"
                  disabled={deleting}
                  onClick={handleDelete}
                  className="rounded px-2 py-0.5 text-xs bg-danger-100 dark:bg-danger-500/20 text-danger-700 dark:text-danger-300 hover:bg-danger-500/30 transition disabled:opacity-50"
                >
                  {deleting ? '…' : t('common.yes')}
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(false)}
                  className="rounded px-2 py-0.5 text-xs bg-panel text-ink-muted hover:bg-raised transition"
                >
                  {t('common.no')}
                </button>
              </div>
            )}
          </div>
        </td>
      </tr>

      {/* Inline plan unlock form */}
      {showUnlock && (
        <tr className="bg-slate-50 dark:bg-white/[0.02]">
          <td colSpan={7} className="px-4 py-4">
            <div className="max-w-md">
              <p className="mb-3 text-sm font-semibold text-ink">{t('admin.unlock.title', 'Plan freischalten')} · {user.email}</p>
              <TierOverrideForm
                kind="user"
                id={user.id}
                initial={{ tier: user.tierOverride ?? null, until: user.tierOverrideUntil ?? null, note: user.tierOverrideNote ?? null }}
                onSaved={(v) => onUpdated(user.id, { tierOverride: v.tier, tierOverrideUntil: v.until, tierOverrideNote: v.note })}
              />
            </div>
          </td>
        </tr>
      )}

      {/* Inline password input */}
      {showPwInput && (
        <tr className="bg-slate-50 dark:bg-white/[0.02]">
          <td colSpan={7} className="px-4 py-2">
            <div className="flex items-center gap-2">
              <input
                type="password"
                minLength={8}
                placeholder={t('superadmin.password.placeholder')}
                value={pwValue}
                onChange={(e) => { setPwValue(e.target.value); setPwMsg(null); }}
                className="rounded-lg bg-panel border border-line px-3 py-1.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 w-64 transition"
              />
              <button
                type="button"
                disabled={pwSaving}
                onClick={handlePwSave}
                className="rounded-lg bg-accent-100 dark:bg-accent-600/30 border border-accent-500/30 px-3 py-1.5 text-xs text-accent-700 dark:text-accent-300 hover:bg-accent-600/40 transition disabled:opacity-50"
              >
                {pwSaving ? '…' : <Check size={14} />}
              </button>
              <button type="button" onClick={() => { setShowPwInput(false); setPwMsg(null); }} className="rounded-lg bg-panel border border-line px-2 py-1.5 text-xs text-ink-muted hover:text-ink transition">
                <X size={14} />
              </button>
              {pwMsg && (
                <span className={`text-xs ${pwMsg.type === 'success' ? 'text-success-300' : 'text-danger-300'}`}>
                  {pwMsg.text}
                </span>
              )}
            </div>
          </td>
        </tr>
      )}

      {/* Expanded card section */}
      {expanded && user.cards.length > 0 && (
        <tr className="bg-slate-50 dark:bg-white/[0.02]">
          <td colSpan={7} className="px-4 py-3">
            <div className="space-y-2">
              {user.cards.map((card) => (
                <div key={card.cardId} className="flex items-center gap-3 rounded-lg bg-panel border border-line px-3 py-2">
                  <span className="text-xs text-ink-muted font-mono truncate">{card.slug}</span>
                  <div className="flex items-center gap-1.5 ml-auto">
                    <input
                      type="text"
                      maxLength={7}
                      value={scValues[card.cardId] ?? card.shortCode}
                      onChange={(e) => {
                        setScValues((v) => ({ ...v, [card.cardId]: e.target.value }));
                        setScMsgs((m) => ({ ...m, [card.cardId]: undefined as unknown as { type: 'success' | 'error'; text: string } }));
                      }}
                      className="w-24 rounded-lg bg-panel border border-line px-2 py-1 text-xs font-mono text-ink focus:outline-none focus:border-accent-500/50 transition"
                    />
                    <button
                      type="button"
                      onClick={() => handleScSave(card.cardId)}
                      className="rounded-lg bg-accent-100 dark:bg-accent-600/20 border border-accent-500/30 px-2 py-1 text-xs text-accent-700 dark:text-accent-300 hover:bg-accent-600/30 transition"
                    >
                      <Check size={12} />
                    </button>
                    {scMsgs[card.cardId] && (
                      <span className={`text-xs ${scMsgs[card.cardId]?.type === 'success' ? 'text-success-300' : 'text-danger-300'}`}>
                        {scMsgs[card.cardId]?.text}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SuperAdminUsersPage() {
  const { t } = useAppTranslation();
  const { user, loading: authLoading } = useAuth();
  const [users, setUsers] = useState<SuperAdminUser[]>([]);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importOrgId, setImportOrgId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [multiEditOpen, setMultiEditOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, orgsRes] = await Promise.all([
        fetch('/api/admin/users/all', { credentials: 'include' }),
        fetch('/api/admin/organisations', { credentials: 'include' }),
      ]);
      const usersData = await usersRes.json() as SuperAdminUser[];
      const orgsData = await orgsRes.json() as { organisations: OrgOption[] };
      setUsers(Array.isArray(usersData) ? usersData : []);
      setOrgs(orgsData.organisations ?? []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadData(); }, [loadData]);

  if (!authLoading && user?.role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  async function handleExport() {
    const url = selectedOrg ? `/api/superadmin/users/export?orgId=${selectedOrg}` : '/api/superadmin/users/export';
    const res = await fetch(url, { credentials: 'include' });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = t('superadmin.exportFilename');
    a.click();
  }

  function handleUpdated(id: string, updates: Partial<SuperAdminUser>) {
    setUsers((prev) => prev.map((u) => u.id === id ? { ...u, ...updates } : u));
  }

  function handleDeleted(id: string) {
    setUsers((prev) => prev.filter((u) => u.id !== id));
  }

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      u.email.toLowerCase().includes(q) ||
      u.firstName.toLowerCase().includes(q) ||
      u.lastName.toLowerCase().includes(q) ||
      u.organisationName.toLowerCase().includes(q);
    const matchOrg = !selectedOrg || u.organisationId === selectedOrg;
    return matchSearch && matchOrg;
  });

  const uniqueOrgCount = new Set(users.map((u) => u.organisationId)).size;

  const selectedUserObjects = useMemo(
    () => users.filter((u) => selected.has(u.id)),
    [users, selected],
  );

  const allFilteredSelected = filtered.length > 0 && filtered.every((u) => selected.has(u.id));

  function toggleAll() {
    if (allFilteredSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((u) => next.delete(u.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((u) => next.add(u.id));
        return next;
      });
    }
  }

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <Layout title={t('superadmin.pageTitle')}>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-ink">{t('superadmin.header.title')}</h1>
            <p className="text-sm text-ink-muted mt-0.5">{t('superadmin.header.subtitle')}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-line bg-panel px-4 py-3">
            <p className="text-2xl font-bold text-ink">{users.length}</p>
            <p className="text-xs text-ink-muted mt-0.5">{t('superadmin.stats.totalUsers')}</p>
          </div>
          <div className="rounded-xl border border-line bg-panel px-4 py-3">
            <p className="text-2xl font-bold text-ink">{uniqueOrgCount}</p>
            <p className="text-xs text-ink-muted mt-0.5">{t('superadmin.stats.organisations')}</p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
            <input
              type="text"
              placeholder={t('superadmin.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block w-full rounded-lg bg-panel border border-line pl-8 pr-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition"
            />
          </div>

          <select
            value={selectedOrg}
            onChange={(e) => setSelectedOrg(e.target.value)}
            className="rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink focus:outline-none focus:border-accent-500/50 transition"
          >
            <option value="">{t('superadmin.allOrganisations')}</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>

          <button type="button" onClick={handleExport} className={btnGhost}>
            <Download size={14} />
            {t('superadmin.export')}
          </button>

          <button type="button" onClick={() => {
            setImportOrgId(selectedOrg || orgs[0]?.id || '');
            setShowImport(true);
          }} className={btnGhost}>
            <Upload size={14} />
            {t('superadmin.import')}
          </button>

          <button type="button" onClick={() => setShowAddUser(true)} className={btnPrimary}>
            <Plus size={14} />
            {t('superadmin.addUser')}
          </button>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="sticky top-2 z-30 rounded-xl border border-accent-500/30 bg-raised/95 backdrop-blur-md shadow-lg shadow-accent-900/20 px-4 py-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-ink">
              <span className="text-accent-700 dark:text-accent-300">{selected.size}</span> {t('users.bulk.selected', '', { count: selected.size })}
            </span>
            <div className="h-4 w-px bg-slate-300 dark:bg-white/10" />
            <button
              onClick={() => setMultiEditOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-panel px-3 py-1.5 text-xs text-ink hover:bg-raised hover:text-ink transition-all"
            >
              <Pencil className="w-3 h-3" /> {t('users.bulk.editCards', 'Edit cards')}
            </button>
            <div className="ml-auto">
              <button
                onClick={() => setSelected(new Set())}
                className="text-xs text-ink-muted hover:text-ink transition-colors"
              >
                {t('users.bulk.clearSelection')}
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="text-center py-16 text-ink-faint">{t('common.loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
            <Users size={32} className="text-ink-faint" />
            <p className="text-ink-muted text-sm">{t('superadmin.empty')}</p>
          </div>
        ) : (
          <div className="rounded-xl border border-line overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-panel">
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAll}
                      className="rounded border-line-strong bg-panel text-accent-500 focus:ring-accent-500/30 cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide">{t('common.name')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide hidden md:table-cell">{t('common.email')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide hidden lg:table-cell">{t('common.fields.organisation')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide">{t('common.role')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide">{t('nav.cards')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-muted uppercase tracking-wide">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-white/5">
                {filtered.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    currentUserId={user?.id ?? ''}
                    isSelected={selected.has(u.id)}
                    onToggle={() => toggleOne(u.id)}
                    onUpdated={handleUpdated}
                    onDeleted={handleDeleted}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddUser && (
        <AddUserDrawer
          orgs={orgs}
          onClose={() => setShowAddUser(false)}
          onCreated={loadData}
        />
      )}

      <BulkUserImport
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        organisationId={importOrgId}
        organisationName={orgs.find((o) => o.id === importOrgId)?.name ?? ''}
        orgOptions={orgs}
        apiCall={(url, options) =>
          fetch(url, { ...options, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(options?.headers ?? {}) } })
        }
        showAlert={(msg, type) => {
          if (type === 'error') window.alert(msg);
        }}
        onImported={() => void loadData()}
      />

      <MultiCardEdit
        isOpen={multiEditOpen}
        onClose={() => setMultiEditOpen(false)}
        selectedUsers={selectedUserObjects}
        onSaved={() => void loadData()}
      />
    </Layout>
  );
}
